import React from 'react';

export interface Section {
  id: string;
  title: string;
  content: React.ReactNode;
}

export interface Reference {
  id: string;
  text: string;
  url?: string;
}

export interface Author {
  name: string;
  title: string;
  avatar?: string;
}

export interface BlogPostData {
  id: string;
  slug: string;
  title: string;
  description: string;
  date: string;
  readTime: string;
  author: Author;
  sections: Section[];
  references: Reference[];
}

interface BlogPostProps {
  data: BlogPostData;
}

const BlogPost: React.FC<BlogPostProps> = ({ data }) => {
  const { title, description, date, readTime, author, sections, references } = data;

  return (
    <div className="min-h-screen bg-background-alt">
      <article className="max-w-4xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-text mb-6 leading-tight">
            {title}
          </h1>
          <p className="text-lg text-text/80 mb-6 max-w-3xl mx-auto">
            {description}
          </p>
          <div className="mt-8 flex items-center justify-center space-x-4 text-sm text-text/60">
            <time dateTime={date.split('-').join('')}>{date}</time>
            <span>•</span>
            <span>{readTime}</span>
          </div>
        </div>

        {/* Content Sections */}
        <div className="prose prose-lg prose-blue max-w-none">
          {sections.map((section, index) => (
            <section key={section.id} className="mb-16 bg-background rounded-lg p-8 shadow-sm">
              <h2 className="text-3xl font-bold text-primary mb-6">
                {section.title}
              </h2>
              <div className="text-lg leading-relaxed">
                {section.content}
              </div>
            </section>
          ))}

          {/* References Section */}
          {references.length > 0 && (
            <section className="mt-16 pt-8 border-t border-border">
              <h2 className="text-3xl font-bold text-primary mb-6">References</h2>
              <ul className="space-y-4">
                {references.map((reference) => (
                  <li key={reference.id} className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    {reference.url ? (
                      <a 
                        href={reference.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="hover:text-primary transition-colors"
                      >
                        {reference.text}
                      </a>
                    ) : (
                      <span>{reference.text}</span>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        {/* Author Bio */}
        <div className="mt-16 pt-8 border-t border-border">
          <div className="flex items-center space-x-4">
            {author.avatar ? (
              <img 
                src={author.avatar} 
                alt={author.name} 
                className="w-16 h-16 rounded-full object-cover"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-2xl text-primary">{author.name.split(' ').map(n => n[0]).join('')}</span>
              </div>
            )}
            <div>
              <h3 className="text-xl font-semibold text-text">{author.name}</h3>
              <p className="text-text/60">{author.title}</p>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
};

export default BlogPost; 